import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MODELS, BUNDLES, getPricing } from "@/lib/products";
import { api } from "@/lib/api";
import { PreOrdersAPI } from "@/lib/supabaseServices";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { CircleNotch } from "@phosphor-icons/react";

export default function ReservationDialog({
  open,
  onOpenChange,
  model,
  bundle,
  plug,
  plug2,
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const bundleData = BUNDLES[bundle];
  const pricing = getPricing(model, bundle);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);

    // Always record the pre-order lead in Supabase first (never lose the lead).
    const preOrderPayload = {
      email,
      name: name || null,
      model,
      bundle,
      plug,
      plug2: plug2 || null,
      total: pricing.price,
    };
    try {
      await PreOrdersAPI.submit(preOrderPayload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[supabase] pre_orders insert failed", e);
    }

    try {
      const amountPaise = Math.round(Number(pricing.price) * 100);
      const result = await openRazorpayCheckout({
        amount: amountPaise,
        currency: "INR",
        name: "Aeonyx Life Sciences",
        description: `CryoVault ${model} · ${bundle}`,
        prefill: { email, name: name || "" },
        notes: {
          email,
          model,
          bundle,
          plug,
          plug2: plug2 || "",
        },
      });
      toast.success("Payment successful", {
        description: `Payment ID: ${result.payment_id}`,
      });
      onOpenChange(false);
      // Redirect to a lightweight success page.
      window.location.href = `/success?payment_id=${encodeURIComponent(result.payment_id)}&order_id=${encodeURIComponent(result.order_id)}`;
    } catch (err) {
      const msg = err?.message || "Payment failed";
      if (msg === "Payment cancelled") {
        toast.info("Payment cancelled");
      } else {
        // Fallback: still save as a reservation so the lead isn't lost.
        try {
          await api.post("/reservations", {
            email,
            name: name || null,
            model,
            bundle,
            plug,
            plug2: plug2 || null,
          });
          toast.success(
            "Reservation saved. We'll be in touch to complete your order.",
          );
          onOpenChange(false);
        } catch {
          toast.error(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-3xl p-0 overflow-hidden bg-white border-slate-200"
        data-testid="reservation-dialog"
      >
        <div className="p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light tracking-tight text-slate-900">
              Reserve your CryoVault
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-sm">
              Enter your email to secure your order. You&apos;ll be redirected
              to a secure checkout.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm">
            <div className="flex justify-between text-slate-700">
              <span>{MODELS[model].name}</span>
              <span className="text-slate-500">
                {plug2 ? `${plug} + ${plug2} plugs` : `${plug} plug`}
              </span>
            </div>
            <div className="flex justify-between text-slate-700 mt-1">
              <span>{bundleData.name}</span>
              <div className="flex items-baseline gap-2">
                <span className="text-slate-400 line-through tabular-nums text-xs">
                  ${pricing.compareAt}
                </span>
                <span className="font-semibold text-slate-900 tabular-nums">
                  ${pricing.price}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="res-name" className="text-xs text-slate-600">
                Full name (optional)
              </Label>
              <Input
                id="res-name"
                data-testid="reservation-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            <div>
              <Label htmlFor="res-email" className="text-xs text-slate-600">
                Email
              </Label>
              <Input
                id="res-email"
                data-testid="reservation-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lab.com"
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-sky-500"
              />
            </div>

            <Button
              type="submit"
              data-testid="reservation-submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-cta-gradient hover:brightness-110 text-white font-semibold shadow-[0_15px_35px_-10px_rgba(2,167,196,0.5)]"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <CircleNotch size={16} weight="bold" className="animate-spin" /> Redirecting…
                </span>
              ) : (
                `Continue to secure checkout · $${pricing.price}`
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
